"use client";

import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  MessageSquare,
  MessageCircle,
  Mail,
  Save,
  X,
  Bell,
} from "lucide-react";
import { AppDispatch, RootState } from "@/store";
import {
  fetchAdminUserSettings,
  updateAdminUserSettingsAsync,
} from "@/store/slices/settings.slice";
import { NotificationSettings } from "@/services/settingsService";

const Notifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const storeId = params?.storeId as string;

  const { adminUser } = useSelector((state: RootState) => state.settings);

  const [editSettings, setEditSettings] = useState<NotificationSettings>({
    whatsapp: {},
    sms: {},
    email: {},
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Comprehensive notification types based on research
  const notificationTypes = [
    // Authentication & Security
    {
      id: "login_success",
      label: "Successful Login",
      category: "Authentication & Security",
    },
    {
      id: "failed_login",
      label: "Failed Login Attempt",
      category: "Authentication & Security",
    },
    {
      id: "password_reset",
      label: "Password Reset Request",
      category: "Authentication & Security",
    },
    {
      id: "account_locked",
      label: "Account Locked",
      category: "Authentication & Security",
    },
    {
      id: "new_device_login",
      label: "New Device Login",
      category: "Authentication & Security",
    },

    // Order Management
    {
      id: "new_order",
      label: "New Order Received",
      category: "Order Management",
    },
    {
      id: "order_confirmed",
      label: "Order Confirmed",
      category: "Order Management",
    },
    {
      id: "order_processing",
      label: "Order Processing",
      category: "Order Management",
    },
    {
      id: "order_shipped",
      label: "Order Shipped",
      category: "Order Management",
    },
    {
      id: "order_delivered",
      label: "Order Delivered",
      category: "Order Management",
    },
    {
      id: "order_cancelled",
      label: "Order Cancelled",
      category: "Order Management",
    },
    { id: "failed_order", label: "Failed Order", category: "Order Management" },
    {
      id: "order_refunded",
      label: "Order Refunded",
      category: "Order Management",
    },

    // Customer Management
    {
      id: "new_customer",
      label: "New Customer Registration",
      category: "Customer Management",
    },
    {
      id: "new_visitor",
      label: "New Website Visitor",
      category: "Customer Management",
    },
    {
      id: "customer_birthday",
      label: "Customer Birthday",
      category: "Customer Management",
    },
    {
      id: "customer_milestone",
      label: "Customer Milestone",
      category: "Customer Management",
    },

    // Cart & Purchase
    {
      id: "abandoned_cart",
      label: "Abandoned Cart",
      category: "Cart & Purchase",
    },
    {
      id: "cart_recovery",
      label: "Cart Recovery Reminder",
      category: "Cart & Purchase",
    },
    {
      id: "payment_failed",
      label: "Payment Failed",
      category: "Cart & Purchase",
    },
    {
      id: "payment_successful",
      label: "Payment Successful",
      category: "Cart & Purchase",
    },

    // Inventory & Products
    {
      id: "low_stock",
      label: "Low Stock Alert",
      category: "Inventory & Products",
    },
    {
      id: "out_of_stock",
      label: "Out of Stock",
      category: "Inventory & Products",
    },
    {
      id: "new_product",
      label: "New Product Added",
      category: "Inventory & Products",
    },
    {
      id: "product_back_in_stock",
      label: "Product Back in Stock",
      category: "Inventory & Products",
    },
    {
      id: "price_drop",
      label: "Price Drop Alert",
      category: "Inventory & Products",
    },

    // Promotions & Offers
    {
      id: "new_offer",
      label: "New Offer Available",
      category: "Promotions & Offers",
    },
    {
      id: "offer_expiring",
      label: "Offer Expiring Soon",
      category: "Promotions & Offers",
    },
    {
      id: "flash_sale",
      label: "Flash Sale Started",
      category: "Promotions & Offers",
    },
    {
      id: "coupon_applied",
      label: "Coupon Applied",
      category: "Promotions & Offers",
    },
    {
      id: "cashback_earned",
      label: "Cashback Earned",
      category: "Promotions & Offers",
    },

    // Shipping & Delivery
    {
      id: "shipment_delayed",
      label: "Shipment Delayed",
      category: "Shipping & Delivery",
    },
    {
      id: "out_for_delivery",
      label: "Out for Delivery",
      category: "Shipping & Delivery",
    },
    {
      id: "delivery_attempt",
      label: "Failed Delivery Attempt",
      category: "Shipping & Delivery",
    },
    {
      id: "delivery_rescheduled",
      label: "Delivery Rescheduled",
      category: "Shipping & Delivery",
    },

    // Reviews & Feedback
    {
      id: "new_review",
      label: "New Product Review",
      category: "Reviews & Feedback",
    },
    {
      id: "review_response",
      label: "Review Response Posted",
      category: "Reviews & Feedback",
    },
    {
      id: "feedback_request",
      label: "Feedback Request",
      category: "Reviews & Feedback",
    },
    {
      id: "rating_received",
      label: "New Rating Received",
      category: "Reviews & Feedback",
    },

    // Support & Communication
    {
      id: "support_ticket",
      label: "New Support Ticket",
      category: "Support & Communication",
    },
    {
      id: "ticket_resolved",
      label: "Support Ticket Resolved",
      category: "Support & Communication",
    },
    {
      id: "live_chat",
      label: "New Live Chat Message",
      category: "Support & Communication",
    },
    {
      id: "inquiry_received",
      label: "General Inquiry Received",
      category: "Support & Communication",
    },

    // Wholesaler & Partner
    {
      id: "new_wholesaler",
      label: "New Wholesaler Registration",
      category: "Wholesaler & Partner",
    },
    {
      id: "wholesale_order",
      label: "New Wholesale Order",
      category: "Wholesaler & Partner",
    },
    {
      id: "partner_payout",
      label: "Partner Payout Processed",
      category: "Wholesaler & Partner",
    },
    {
      id: "vendor_application",
      label: "New Vendor Application",
      category: "Wholesaler & Partner",
    },

    // System & Admin
    {
      id: "system_maintenance",
      label: "System Maintenance",
      category: "System & Admin",
    },
    {
      id: "backup_completed",
      label: "Backup Completed",
      category: "System & Admin",
    },
    {
      id: "high_traffic",
      label: "High Traffic Alert",
      category: "System & Admin",
    },
    {
      id: "security_alert",
      label: "Security Alert",
      category: "System & Admin",
    },
    {
      id: "daily_report",
      label: "Daily Sales Report",
      category: "System & Admin",
    },
  ];

  // Group notifications by category
  const groupedNotifications = notificationTypes.reduce(
    (groups, notification) => {
      if (!groups[notification.category]) {
        groups[notification.category] = [];
      }
      groups[notification.category].push(notification);
      return groups;
    },
    {} as { [key: string]: typeof notificationTypes },
  );

  // Fetch settings on component mount
  useEffect(() => {
    // Only fetch if we don't have admin user settings
    if (storeId && !adminUser) {
      dispatch(fetchAdminUserSettings(storeId));
    }
  }, [dispatch, storeId, adminUser]);

  // Initialize edit state when data is loaded
  useEffect(() => {
    if (adminUser.data?.notifications) {
      setEditSettings(adminUser.data.notifications);
    }
  }, [adminUser.data]);

  // Check for changes
  useEffect(() => {
    if (adminUser.data?.notifications) {
      const originalSettings = adminUser.data.notifications;
      const hasWhatsAppChanges =
        JSON.stringify(originalSettings.whatsapp) !==
        JSON.stringify(editSettings.whatsapp);
      const hasSmsChanges =
        JSON.stringify(originalSettings.sms) !==
        JSON.stringify(editSettings.sms);
      const hasEmailChanges =
        JSON.stringify(originalSettings.email) !==
        JSON.stringify(editSettings.email);

      setHasChanges(hasWhatsAppChanges || hasSmsChanges || hasEmailChanges);
    }
  }, [adminUser.data, editSettings]);

  const handleToggleChange = (
    channel: "whatsapp" | "sms" | "email",
    notificationId: string,
    enabled: boolean,
  ) => {
    setEditSettings((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [notificationId]: enabled,
      },
    }));
  };

  const handleSave = async () => {
    if (!storeId) return;

    try {
      await dispatch(
        updateAdminUserSettingsAsync({
          storeId,
          settings: {
            notifications: editSettings,
          },
        }),
      ).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save notification settings:", error);
    }
  };

  const handleCancel = () => {
    if (adminUser.data?.notifications) {
      setEditSettings(adminUser.data.notifications);
      setHasChanges(false);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "whatsapp":
        return <MessageCircle className="h-5 w-5 text-green-600" />;
      case "sms":
        return <MessageSquare className="h-5 w-5 text-blue-600" />;
      case "email":
        return <Mail className="h-5 w-5 text-purple-600" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case "whatsapp":
        return "bg-green-500/10";
      case "sms":
        return "bg-blue-500/10";
      case "email":
        return "bg-purple-500/10";
      default:
        return "bg-gray-500/10";
    }
  };

  if (adminUser.loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (adminUser.error) {
    return (
      <div className="text-center p-6">
        <p className="text-destructive">
          Error loading notification settings: {adminUser.error}
        </p>
        <Button
          onClick={() => storeId && dispatch(fetchAdminUserSettings(storeId))}
          className="mt-4"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="pl-4 mb-6">
        <h2 className="text-xl font-semibold">Notification Settings</h2>
        <p className="text-muted-foreground text-sm">
          Configure notification preferences for different channels
        </p>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* WhatsApp Column */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div
                className={`h-10 w-10 ${getChannelColor("whatsapp")} rounded-lg flex items-center justify-center`}
              >
                {getChannelIcon("whatsapp")}
              </div>
              <div>
                <CardTitle>WhatsApp</CardTitle>
                <CardDescription>
                  WhatsApp Business notifications
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(groupedNotifications).map(
              ([category, notifications]) => (
                <div key={`whatsapp-${category}`}>
                  <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
                    {category}
                  </h4>
                  <div className="space-y-3">
                    {notifications.map((notification) => (
                      <div
                        key={`whatsapp-${notification.id}`}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-foreground">
                          {notification.label}
                        </span>
                        <Switch
                          checked={
                            editSettings.whatsapp?.[notification.id] || false
                          }
                          onCheckedChange={(checked) =>
                            handleToggleChange(
                              "whatsapp",
                              notification.id,
                              checked,
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </CardContent>
        </Card>

        {/* SMS Column */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div
                className={`h-10 w-10 ${getChannelColor("sms")} rounded-lg flex items-center justify-center`}
              >
                {getChannelIcon("sms")}
              </div>
              <div>
                <CardTitle>SMS</CardTitle>
                <CardDescription>Text message notifications</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(groupedNotifications).map(
              ([category, notifications]) => (
                <div key={`sms-${category}`}>
                  <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
                    {category}
                  </h4>
                  <div className="space-y-3">
                    {notifications.map((notification) => (
                      <div
                        key={`sms-${notification.id}`}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-foreground">
                          {notification.label}
                        </span>
                        <Switch
                          checked={editSettings.sms?.[notification.id] || false}
                          onCheckedChange={(checked) =>
                            handleToggleChange("sms", notification.id, checked)
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </CardContent>
        </Card>

        {/* Email Column */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div
                className={`h-10 w-10 ${getChannelColor("email")} rounded-lg flex items-center justify-center`}
              >
                {getChannelIcon("email")}
              </div>
              <div>
                <CardTitle>Email</CardTitle>
                <CardDescription>Email notifications</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(groupedNotifications).map(
              ([category, notifications]) => (
                <div key={`email-${category}`}>
                  <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
                    {category}
                  </h4>
                  <div className="space-y-3">
                    {notifications.map((notification) => (
                      <div
                        key={`email-${notification.id}`}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-foreground">
                          {notification.label}
                        </span>
                        <Switch
                          checked={
                            editSettings.email?.[notification.id] || false
                          }
                          onCheckedChange={(checked) =>
                            handleToggleChange(
                              "email",
                              notification.id,
                              checked,
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save/Cancel Buttons */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 bg-background border border-border rounded-lg p-3 shadow-lg">
          <Button
            onClick={handleSave}
            size="sm"
            className="flex items-center gap-2"
            disabled={adminUser.loading}
          >
            <Save className="w-4 h-4" />
            {adminUser.loading ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default Notifications;
